import { useFunnel } from "@airhelp/funnel";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { useTranslation } from "react-i18next";
import { ControlledAirportAutocomplete } from "../../../modules/form/ControlledAirportsAutocomplete";
import { Flex } from "@chakra-ui/react";
import { AirplaneLanding, AirplaneTakeOff, Info } from "@airhelp/icons";
import {
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import { InfoBox } from "@airhelp/react";
import TranslationWithHTML from "../../../../components/ui-elements/translation-with-html/TranslationWithHTML";
import { ControlledRadioGroup, useForm, useFormHandlers } from "../../../modules/form";
import { sendInfoReport } from "../../../../utils/reporting";
import { z } from "zod";
import {
  getArrivalAirport,
  getDepartureAirport,
  groupItineraryFlights,
  updateFlightDetails,
  updateItineraryFlights,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { airportSchema } from "./FlightDetails";
import { ConnectionsControl } from "v2/modules/form/ConnectionsControl";
import { Airport } from "v2/modules/types/intake-funnel";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { MOTIVATORS_EXPERIMENT_CONFIG } from "../../../../ab-tests/optimizely-experiments";
import { getIsExperimentActive } from "../../../../tracking/tools/optimizely/optimizelyState";
import { pushOptimizelyEvent } from "../../../../tracking/tools/optimizely/events";
import { useAppSettingsSnapshot } from "v2/settings";
import { isRtl } from "utils/getLocaleDirection";
import { useFeature } from "@growthbook/growthbook-react";

const options = ["no", "yes"] as const;

const defaultStepSchema = z.object({
  departureAirport: airportSchema.nullable(),
  arrivalAirport: airportSchema.nullable(),
  hasConnections: z.enum(options).optional(),
  connections: z.array(z.object({ airport: airportSchema.nullable() })),
});

const stepSchema = z
  .object({
    hasConnections: z.enum(options),
    departureAirport: airportSchema,
    arrivalAirport: airportSchema,
    connections: z.array(z.object({ airport: airportSchema })),
  })
  .refine((data) => {
    if (data.hasConnections === "no") {
      return true;
    }
    return data.connections.length > 0;
  });

export function TripDetailsStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { flightDetails } = usePreEligibilityDataSnapshot();
  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });
  const { locale } = useAppSettingsSnapshot();

  const departureAirport = getDepartureAirport();
  const arrivalAirport = getArrivalAirport();

  const isMotivatorsExperimentActive = getIsExperimentActive(MOTIVATORS_EXPERIMENT_CONFIG);

  const {
    control,
    handleSubmit,
    watch,
    resetField,
    formState: { isValid, isSubmitting, isDirty },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      departureAirport: departureAirport,
      arrivalAirport: arrivalAirport,
      hasConnections: flightDetails.anyConnections ? ("yes" as const) : ("no" as const),
      connections: flightDetails.connections as { airport: Airport }[],
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    onSubmit({ arrivalAirport, departureAirport, hasConnections, connections }) {
      const validConnections = connections.filter((item) => item.airport !== null) as {
        airport: Airport;
      }[];
      const isConnections = hasConnections === "yes";

      if (arrivalAirport && departureAirport) {
        updateFlightDetails({
          anyConnections: isConnections,
          connections: validConnections,
        });

        const groupedItineraryFlights = groupItineraryFlights({
          departureAirport,
          arrivalAirport,
          connections: validConnections,
        });

        if (isDirty) {
          updateItineraryFlights(groupedItineraryFlights);
        }
      } else {
        sendInfoReport("Missing airport data on submit of flight details step");
      }

      isMotivatorsExperimentActive &&
        pushOptimizelyEvent(MOTIVATORS_EXPERIMENT_CONFIG.conversion_events?.[0]);

      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      trackEnquiryStepComplete();
      funnel.stepForward();
    },
  });
  const reactiveArrivalAirport = watch("arrivalAirport");
  const reactiveDepartureAirport = watch("departureAirport");
  const showAnyConnections = watch("hasConnections") === "yes";

  useFeature("webapp-funnel-aa-test");
  useFeature("webapp-funnel-aa-test-prod");

  return (
    <FunnelStepContainer data-testid="tripControl">
      <FunnelStepSection title={t("steps.flight_details.text")}>
        <Flex
          flexDir={{ base: "column", md: "row" }}
          justifyContent={{ base: "flex-start", md: "space-between" }}
          data-sentry-unmask
        >
          <ControlledAirportAutocomplete
            control={control}
            name={"departureAirport"}
            dataTestId="departureAirportAutocomplete"
            label={t("components.airline_autocomplete.departed_label")}
            placeholder={t("components.airline_autocomplete.departed_placeholder")}
            formControlStyles={{
              mb: { base: 2, md: 0 },
              maxWidth: { base: "100%", md: "calc(50% - 8px)" },
            }}
            icon={<AirplaneTakeOff boxSize={6} />}
            isOptionDisabled={(option) => option.value.iata === reactiveArrivalAirport?.iata}
            isRtl={isRtl(locale)}
          />
          <ControlledAirportAutocomplete
            control={control}
            name={"arrivalAirport"}
            dataTestId="arrivalAirportAutocomplete"
            label={t("components.airline_autocomplete.arrival_label")}
            placeholder={t("components.airline_autocomplete.arrival_placeholder")}
            formControlStyles={{
              maxWidth: { base: "100%", md: "calc(50% - 8px)" },
            }}
            icon={<AirplaneLanding boxSize={6} />}
            isOptionDisabled={(option) => option.value.iata === reactiveDepartureAirport?.iata}
            isRtl={isRtl(locale)}
          />
        </Flex>
      </FunnelStepSection>

      <FunnelStepSection
        title={t("steps.any_connections.text", {
          departure: departureAirport?.city,
          departure_iata: departureAirport?.iata,
          arrival: arrivalAirport?.city,
          arrival_iata: arrivalAirport?.iata,
        })}
      >
        <ControlledRadioGroup
          name={"hasConnections"}
          data-testid="anyConnections"
          control={control}
          options={options}
          renderOption={(option) => t(`steps.any_connections.answer_${option}`)}
          onChange={() => resetField("connections", { defaultValue: [] })}
          flexDir={{ base: "column", md: "row" }}
          gap={{ base: "2", md: "4" }}
        />
      </FunnelStepSection>
      {showAnyConnections && (
        <FunnelStepSection title={t("steps.connections.text")}>
          <ConnectionsControl control={control} name={"connections"} />
        </FunnelStepSection>
      )}

      <InfoBox isChat icon={<Info boxSize={5} color="primary.500" />}>
        <TranslationWithHTML text={t("steps.flight_details.hint")} />
      </InfoBox>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
